import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCLoader } from 'rollup-allspark';
import { COMPARISON_TYPE } from '../../../config/constants/analytics';
import { isEmpty } from '../../../utils';
import { makeSelectProperty } from '../selectors';
import * as selectors from '../selectors';
import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
import { SERIES_PALETTE } from '../theme/tokens';

const DONUT_MIN_HEIGHT = 280;
const LEGEND_ITEM_HEIGHT = 22;

const centeredPlaceholderStyle = { minHeight: DONUT_MIN_HEIGHT };

const chartHeightForLabels = labelCount => {
  if (!labelCount) return DONUT_MIN_HEIGHT;
  const legendStackHeight = labelCount * LEGEND_ITEM_HEIGHT + 32;
  return Math.max(DONUT_MIN_HEIGHT, legendStackHeight);
};

const BusyModeReasonPieChart = ({
  busyModeReasonData,
  busyModeLogDataLoading,
  selectedComparisonType,
}) => {
  const [pieChartData, setPieChartData] = useState({});

  useEffect(() => {
    if (isEmpty(busyModeReasonData)) {
      setPieChartData({});
      return;
    }

    const series = [];
    const labels = [];
    const tooltips = [];

    const entityKey =
      selectedComparisonType === COMPARISON_TYPE.BRAND ? 'brands' : 'branches';

    Object.keys(busyModeReasonData).forEach(reason => {
      const entities = busyModeReasonData[reason][entityKey];
      const total = entities.reduce((acc, entity) => acc + entity.count, 0);
      labels.push(`${reason} (${total})`);
      series.push(total);
      tooltips.push({ reason, total, entities });
    });

    setPieChartData({ series, labels, tooltips });
  }, [busyModeReasonData, selectedComparisonType]);

  const formatTotalValue = w => {
    const total = w.globals.seriesTotals.reduce((a, b) => +a + +b, 0);
    return total;
  };

  const chartHeight = chartHeightForLabels(pieChartData.labels?.length || 0);

  const chartConfig = useMemo(
    () => ({
      options: mergeChartOptions(getBaseChartOptions(), {
        colors: SERIES_PALETTE,
        labels: pieChartData.labels || [],
        stroke: { show: false, width: 0 },
        dataLabels: { enabled: false },
        legend: {
          show: true,
          position: 'right',
          horizontalAlign: 'center',
          fontSize: '11px',
          markers: { width: 8, height: 8, radius: 8 },
          itemMargin: { horizontal: 4, vertical: 3 },
          onItemClick: { toggleDataSeries: true },
          onItemHover: { highlightDataSeries: true },
        },
        tooltip: {
          custom({ seriesIndex }) {
            const entry = pieChartData.tooltips?.[seriesIndex];
            if (!entry) return '';

            const { reason, total, entities } = entry;
            const visible = entities.slice(0, 8);
            const hiddenCount = entities.length - visible.length;

            const rows = visible
              .map(
                entity =>
                  `<div style="display:flex;justify-content:space-between;gap:16px;padding:2px 0;">
                    <span>${entity.title}</span>
                    <span style="font-weight:600;">${entity.count}</span>
                  </div>`,
              )
              .join('');

            const moreRow =
              hiddenCount > 0
                ? `<div style="margin-top:6px;font-size:11px;color:#6b7280;">+ ${hiddenCount} more</div>`
                : '';

            return `<div style="padding:10px 12px;min-width:180px;max-width:280px;">
              <div style="font-weight:600;margin-bottom:8px;">${reason} (${total})</div>
              <div style="max-height:200px;overflow-y:auto;">${rows}${moreRow}</div>
            </div>`;
          },
        },
        plotOptions: {
          pie: {
            offsetX: -16,
            donut: {
              size: '68%',
              labels: {
                show: true,
                name: { show: false },
                value: { show: true },
                total: {
                  show: true,
                  showAlways: true,
                  formatter: formatTotalValue,
                },
              },
            },
          },
        },
        chart: {
          type: 'donut',
          height: chartHeight,
          animations: { enabled: false },
          nodata: {
            text: 'No data',
            style: { color: '#000000', fontSize: '16px' },
          },
        },
      }),
      series: pieChartData.series || [],
    }),
    [pieChartData, chartHeight],
  );

  if (busyModeLogDataLoading) {
    return (
      <div
        className="flex w-full items-center justify-center"
        style={centeredPlaceholderStyle}
      >
        <SCLoader lineWidth={5} diameter={50} />
      </div>
    );
  }

  if (isEmpty(pieChartData.series)) {
    return (
      <div
        className="flex w-full items-center justify-center text-2xl text-grey-darker"
        style={centeredPlaceholderStyle}
      >
        No data found
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center mt-4 lg:mb-4 md:mb-0">
      <div className="relative w-full">
        <Chart
          options={chartConfig.options}
          series={chartConfig.series}
          type="donut"
          width="100%"
          height={chartHeight}
        />
      </div>
    </div>
  );
};

BusyModeReasonPieChart.propTypes = {
  busyModeReasonData: PropTypes.object,
  busyModeLogDataLoading: PropTypes.bool,
  selectedComparisonType: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  busyModeReasonData: makeSelectProperty('busyModeReasonData'),
  busyModeLogDataLoading: selectors.makeSelectBusyModeLogDataLoading(),
  selectedComparisonType: makeSelectProperty('selectedComparisonType'),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(BusyModeReasonPieChart);
